import { useState, useEffect } from 'react';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useIsMobile } from '../hooks/useIsMobile';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

const STATUT_COLORS = { Prospect: '#41B073', 'Client actif': '#22c55e', 'Client inactif': '#94a3b8', Perdu: '#ef4444' };
const TYPE_COLORS = { TPE: '#3b82f6', PME: '#41B073', ETI: '#8b5cf6', Partenaire: '#f59e0b' };

const Badge = ({ text, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{text}</span>
);

const Overlay = ({ onClick }) => (
  <div onClick={onClick} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
);

const Field = ({ label, value }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2, fontWeight: 500, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 13, color: '#e2e8f0' }}>{value || '—'}</div>
  </div>
);

export default function ClientPanel({ clientId, onClose }) {
  const fetchWithAuth = useAuthFetch();
  const isMobile = useIsMobile();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState('infos');
  const [editing, setEditing] = useState(false);
  const [editingRgpd, setEditingRgpd] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [opportunites, setOpportunites] = useState([]);
  const [devis, setDevis] = useState([]);
  const [factures, setFactures] = useState([]);
  const [audits, setAudits] = useState([]);

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`/api/clients/${clientId}`)
      .then(r => r.json())
      .then(data => { setClient(data); setForm(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (tab === 'contacts' && contacts.length === 0)
      fetchWithAuth(`/api/contacts?client_id=${clientId}&limit=50`).then(r => r.json()).then(d => setContacts(d.data || []));
    if (tab === 'contrats' && contrats.length === 0)
      fetchWithAuth(`/api/contrats?client_id=${clientId}&limit=50`).then(r => r.json()).then(d => setContrats(d.data || []));
    if (tab === 'opportunites' && opportunites.length === 0)
      fetchWithAuth(`/api/opportunites?client_id=${clientId}&limit=50`).then(r => r.json()).then(d => setOpportunites(d.data || []));
    if (tab === 'devis' && devis.length === 0)
      fetchWithAuth(`/api/devis?client_id=${clientId}&limit=50`).then(r => r.json()).then(d => setDevis(d.data || []));
    if (tab === 'factures' && factures.length === 0)
      fetchWithAuth(`/api/factures?client_id=${clientId}&limit=50`).then(r => r.json()).then(d => setFactures(d.data || []));
    if (tab === 'audits' && audits.length === 0)
      fetchWithAuth(`/api/audits?client_id=${clientId}&limit=50`).then(r => r.json()).then(d => setAudits(d.data || []));
  }, [tab]);

  const save = async () => {
    setSaving(true); setSaveError(null);
    try {
      const res = await fetchWithAuth(`/api/clients/${clientId}`, { method: 'PUT', body: JSON.stringify(form) });
      const updated = await res.json();
      if (!res.ok) { setSaveError(updated.error || 'Erreur lors de la sauvegarde'); return; }
      setClient(updated); setEditing(false);
    } catch (e) { setSaveError(e.message);
    } finally { setSaving(false); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, background: '#0f1117', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  const TABS = [{ id: 'infos', label: 'Infos' }, { id: 'contacts', label: 'Contacts' }, { id: 'contrats', label: 'Contrats' }, { id: 'opportunites', label: 'Opps.' }, { id: 'devis', label: 'Devis' }, { id: 'factures', label: 'Factures' }, { id: 'audits', label: 'Audits' }, { id: 'rgpd', label: '🔒 RGPD' }];

  const saveRgpd = async () => {
    setSaving(true); setSaveError(null);
    try {
      const res     = await fetchWithAuth(`/api/clients/${clientId}`, { method: 'PUT', body: JSON.stringify(form) });
      const updated = await res.json();
      if (!res.ok) { setSaveError(updated.error || 'Erreur lors de la sauvegarde'); return; }
      setClient(updated); setEditingRgpd(false);
    } catch (e) { setSaveError(e.message);
    } finally { setSaving(false); }
  };

  const SENSIBILITE_C = { Standard: '#64748b', Sensible: '#f59e0b', Critique: '#ef4444' };
  const TYPE_DONNEES_OPTIONS = ['RH', 'Clients', 'Financières', 'Santé', 'Autre'];

  const ETAPE_C = { Qualification: '#41B073', Proposition: '#3b82f6', Négociation: '#f59e0b', Gagnée: '#22c55e', Perdue: '#ef4444' };
  const STATUT_F = { Brouillon: '#94a3b8', Envoyé: '#3b82f6', Envoyée: '#3b82f6', Accepté: '#22c55e', Refusé: '#ef4444', Expiré: '#f59e0b', Payée: '#22c55e', 'En retard': '#ef4444', Annulée: '#475569' };

  return (
    <>
      <Overlay onClick={onClose} />
      <div style={{ position: 'fixed', top: 0, right: 0, width: isMobile ? '100vw' : 440, height: '100vh', background: '#1a1d2e', borderLeft: '1px solid #2a2d3e', zIndex: 100, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '20px 20px 0', borderBottom: '1px solid #2a2d3e', paddingBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#e2e8f0', marginBottom: 6 }}>{loading ? '...' : client?.nom}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {client && <Badge text={client.type} color={TYPE_COLORS[client.type] || '#41B073'} />}
                {client && <Badge text={client.statut} color={STATUT_COLORS[client.statut] || '#64748b'} />}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>
          <div className="tabs-scroll" style={{ display: 'flex', gap: 2, overflowX: 'auto' }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '7px 12px', borderRadius: '6px 6px 0 0', border: 'none', background: tab === t.id ? '#41B07320' : 'transparent', color: tab === t.id ? '#41B073' : '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: tab === t.id ? 600 : 400, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading ? <div style={{ color: '#64748b' }}>Chargement...</div> : (
            <>
              {tab === 'infos' && (
                <div>
                  {!editing ? (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 16 }}>
                        <Field label="Secteur" value={client.secteur} />
                        <Field label="Pays" value={client.pays} />
                        <Field label="Ville" value={client.ville} />
                        <Field label="Code postal" value={client.code_postal} />
                      </div>
                      <Field label="Adresse" value={client.adresse} />
                      <Field label="Téléphone" value={client.telephone} />
                      <Field label="Email" value={client.email} />
                      <Field label="Site web" value={client.site_web} />
                      <Field label="Notes" value={client.notes} />
                      <Field label="Commercial" value={client.commercial_nom} />
                      <Field label="Créé le" value={fmtDate(client.created_at)} />
                      <button onClick={() => setEditing(true)} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>✏️ Modifier</button>
                    </>
                  ) : (
                    <div>
                      {[['nom', 'Nom'], ['type', 'Type'], ['statut', 'Statut'], ['secteur', 'Secteur'], ['adresse', 'Adresse'], ['ville', 'Ville'], ['code_postal', 'Code postal'], ['telephone', 'Téléphone'], ['email', 'Email'], ['site_web', 'Site web']].map(([k, l]) => (
                        <div key={k} style={{ marginBottom: 12 }}>
                          <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>{l}</label>
                          {['type', 'statut'].includes(k) ? (
                            <select value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={inputStyle}>
                              {k === 'type' && ['TPE', 'PME', 'ETI', 'Partenaire'].map(v => <option key={v}>{v}</option>)}
                              {k === 'statut' && ['Prospect', 'Client actif', 'Client inactif', 'Perdu'].map(v => <option key={v}>{v}</option>)}
                            </select>
                          ) : (
                            <input value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={inputStyle} />
                          )}
                        </div>
                      ))}
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Notes</label>
                        <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inputStyle, height: 80, resize: 'vertical' }} />
                      </div>
                      {saveError && <div style={{ marginBottom: 8, padding: '6px 10px', background: '#ef444422', border: '1px solid #ef444444', borderRadius: 6, color: '#ef4444', fontSize: 12 }}>{saveError}</div>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={save} disabled={saving} style={{ padding: '8px 20px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>{saving ? 'Enregistrement...' : '💾 Enregistrer'}</button>
                        <button onClick={() => { setEditing(false); setForm(client); setSaveError(null); }} style={{ padding: '8px 20px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Annuler</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {tab === 'contacts' && (
                <div>
                  {contacts.length === 0 ? <div style={{ color: '#64748b', fontSize: 13 }}>Aucun contact</div> : contacts.map(c => (
                    <div key={c.id} style={{ padding: '10px 12px', background: '#0f111780', borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#e2e8f0' }}>{c.prenom} {c.nom} {c.principal ? '⭐' : ''}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{c.poste} {c.email ? `· ${c.email}` : ''}</div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'contrats' && (
                <div>
                  {contrats.length === 0 ? <div style={{ color: '#64748b', fontSize: 13 }}>Aucun contrat</div> : contrats.map(c => (
                    <div key={c.id} style={{ padding: '10px 12px', background: '#0f111780', borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#e2e8f0', marginBottom: 4 }}>{c.titre}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#64748b' }}>{c.type} · {fmt(c.montant_ht)} HT</span>
                        <Badge text={c.statut} color={{ Actif: '#22c55e', Expiré: '#ef4444', Résilié: '#94a3b8', 'En attente': '#f59e0b' }[c.statut] || '#64748b'} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'opportunites' && (
                <div>
                  {opportunites.length === 0 ? <div style={{ color: '#64748b', fontSize: 13 }}>Aucune opportunité</div> : opportunites.map(o => (
                    <div key={o.id} style={{ padding: '10px 12px', background: '#0f111780', borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#e2e8f0', marginBottom: 4 }}>{o.titre}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#41B073', fontFamily: 'DM Mono, monospace' }}>{fmt(o.valeur)}</span>
                        <Badge text={o.etape} color={ETAPE_C[o.etape] || '#64748b'} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'devis' && (
                <div>
                  {devis.length === 0 ? <div style={{ color: '#64748b', fontSize: 13 }}>Aucun devis</div> : devis.map(d => (
                    <div key={d.id} style={{ padding: '10px 12px', background: '#0f111780', borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#94a3b8', marginBottom: 2 }}>{d.numero}</div>
                      <div style={{ fontSize: 13, color: '#e2e8f0', marginBottom: 4 }}>{d.titre}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#41B073', fontFamily: 'DM Mono, monospace' }}>{fmt(d.montant_ht)} HT</span>
                        <Badge text={d.statut} color={STATUT_F[d.statut] || '#64748b'} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'factures' && (
                <div>
                  {factures.length === 0 ? <div style={{ color: '#64748b', fontSize: 13 }}>Aucune facture</div> : factures.map(f => (
                    <div key={f.id} style={{ padding: '10px 12px', background: '#0f111780', borderRadius: 8, marginBottom: 8, borderLeft: f.statut === 'En retard' ? '3px solid #ef4444' : '3px solid transparent' }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#94a3b8', marginBottom: 2 }}>{f.numero}</div>
                      <div style={{ fontSize: 13, color: '#e2e8f0', marginBottom: 4 }}>{f.titre}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#41B073', fontFamily: 'DM Mono, monospace' }}>{fmt(f.montant_ht)} HT</span>
                        <Badge text={f.statut} color={STATUT_F[f.statut] || '#64748b'} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'rgpd' && (
                <div>
                  {!editingRgpd ? (
                    <>
                      {/* Niveau de sensibilité */}
                      {client.rgpd_sensibilite && (
                        <div style={{ background: (SENSIBILITE_C[client.rgpd_sensibilite] || '#64748b') + '15', border: `1px solid ${(SENSIBILITE_C[client.rgpd_sensibilite] || '#64748b')}44`, borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2, fontWeight: 600, textTransform: 'uppercase' }}>Niveau de sensibilité</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: SENSIBILITE_C[client.rgpd_sensibilite] || '#64748b' }}>{client.rgpd_sensibilite}</div>
                        </div>
                      )}
                      <Field label="Base légale du traitement" value={client.rgpd_base_legale} />
                      <Field label="DPA signé" value={client.rgpd_dpa_signe ? `✅ Oui${client.rgpd_dpa_date ? ` — ${fmtDate(client.rgpd_dpa_date)}` : ''}` : '❌ Non'} />
                      <Field label="Données personnelles impliquées" value={client.rgpd_donnees_perso ? 'Oui' : 'Non'} />
                      {client.rgpd_type_donnees?.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, fontWeight: 500, textTransform: 'uppercase' }}>Types de données</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {client.rgpd_type_donnees.map(t => (
                              <span key={t} style={{ background: '#41B07322', color: '#41B073', border: '1px solid #41B07344', borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600 }}>{t}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {client.rgpd_notes && <Field label="Notes RGPD" value={client.rgpd_notes} />}
                      <button onClick={() => setEditingRgpd(true)} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>✏️ Modifier</button>
                    </>
                  ) : (
                    <div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Base légale du traitement</label>
                        <select value={form.rgpd_base_legale || ''} onChange={e => setForm(f => ({ ...f, rgpd_base_legale: e.target.value }))} style={inputStyle}>
                          <option value="">Non renseigné</option>
                          {['Contrat', 'Consentement', 'Intérêt légitime', 'Obligation légale'].map(v => <option key={v}>{v}</option>)}
                        </select>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Niveau de sensibilité</label>
                        <select value={form.rgpd_sensibilite || ''} onChange={e => setForm(f => ({ ...f, rgpd_sensibilite: e.target.value }))} style={inputStyle}>
                          <option value="">Non renseigné</option>
                          {['Standard', 'Sensible', 'Critique'].map(v => <option key={v}>{v}</option>)}
                        </select>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#e2e8f0', cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!form.rgpd_dpa_signe} onChange={e => setForm(f => ({ ...f, rgpd_dpa_signe: e.target.checked }))} />
                          DPA signé
                        </label>
                      </div>
                      {form.rgpd_dpa_signe && (
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Date de signature DPA</label>
                          <input type="date" value={form.rgpd_dpa_date || ''} onChange={e => setForm(f => ({ ...f, rgpd_dpa_date: e.target.value }))} style={{ ...inputStyle, colorScheme: 'dark' }} />
                        </div>
                      )}
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#e2e8f0', cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!form.rgpd_donnees_perso} onChange={e => setForm(f => ({ ...f, rgpd_donnees_perso: e.target.checked }))} />
                          Données personnelles impliquées
                        </label>
                      </div>
                      {form.rgpd_donnees_perso && (
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Types de données</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {TYPE_DONNEES_OPTIONS.map(t => (
                              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>
                                <input type="checkbox"
                                  checked={(form.rgpd_type_donnees || []).includes(t)}
                                  onChange={e => setForm(f => ({
                                    ...f,
                                    rgpd_type_donnees: e.target.checked
                                      ? [...(f.rgpd_type_donnees || []), t]
                                      : (f.rgpd_type_donnees || []).filter(x => x !== t)
                                  }))} />
                                {t}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Notes RGPD</label>
                        <textarea value={form.rgpd_notes || ''} onChange={e => setForm(f => ({ ...f, rgpd_notes: e.target.value }))} style={{ ...inputStyle, height: 60, resize: 'vertical' }} />
                      </div>
                      {saveError && <div style={{ marginBottom: 8, padding: '6px 10px', background: '#ef444422', border: '1px solid #ef444444', borderRadius: 6, color: '#ef4444', fontSize: 12 }}>{saveError}</div>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={saveRgpd} disabled={saving} style={{ padding: '8px 20px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>{saving ? 'Enregistrement…' : '💾 Enregistrer'}</button>
                        <button onClick={() => { setEditingRgpd(false); setForm(client); setSaveError(null); }} style={{ padding: '8px 20px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Annuler</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {tab === 'audits' && (
                <div>
                  {audits.length === 0 ? <div style={{ color: '#64748b', fontSize: 13 }}>Aucun audit</div> : audits.map(a => {
                    const scoreFields = ['qualite_donnees', 'accessibilite_donnees', 'structuration_donnees', 'maturite_data_global', 'maturite_digitale'];
                    const filled = scoreFields.filter(f => Number(a[f]) > 0);
                    const score = filled.length > 0 ? Math.round(scoreFields.reduce((acc, f) => acc + (Number(a[f]) || 0), 0) / 25 * 100) : null;
                    const LEVEL_COLOR = score === null ? '#64748b' : score <= 20 ? '#ef4444' : score <= 40 ? '#f97316' : score <= 60 ? '#eab308' : score <= 80 ? '#22c55e' : '#41B073';
                    const STATUT_A = { Froid: '#94a3b8', Tiède: '#f59e0b', Chaud: '#f97316', Confirmé: '#22c55e', Perdu: '#ef4444' };
                    return (
                      <div key={a.id} style={{ padding: '10px 12px', background: '#0f111780', borderRadius: 8, marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{a.date_audit ? new Date(a.date_audit).toLocaleDateString('fr-FR') : '—'}</div>
                          {a.statut_prospect && <Badge text={a.statut_prospect} color={STATUT_A[a.statut_prospect] || '#64748b'} />}
                        </div>
                        {score !== null && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <div style={{ flex: 1, height: 4, background: '#2a2d3e', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${score}%`, background: LEVEL_COLOR, borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: LEVEL_COLOR, fontFamily: 'DM Mono, monospace' }}>{score}%</span>
                          </div>
                        )}
                        {a.livrable_recommande && <div style={{ fontSize: 12, color: '#41B073' }}>{a.livrable_recommande}</div>}
                        {a.estimation_indicative && <div style={{ fontSize: 11, color: '#22c55e', marginTop: 2 }}>{a.estimation_indicative}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
